import type { FrameQuality } from "../types.js";
import { clahe } from "./clahe.js";
import { medianDenoise3x3 } from "./denoise.js";
import { flattenIllumination, softClipHighlights } from "./illumination.js";
import { unsharpMask } from "./sharpen.js";

export { toLuma, lumaToImageData } from "./luma.js";
export { analyzeFrame, laplacianVariance } from "./exposure.js";
export { TemporalDenoiser } from "./temporalDenoise.js";
export { medianDenoise3x3 } from "./denoise.js";
export { clahe, flattenIllumination, softClipHighlights, unsharpMask };

/**
 * Pick and run an enhancement chain suited to *why* the frame is hard to
 * read, rather than always running every filter (which would waste CPU
 * budget and, for CLAHE especially, amplify noise on frames that didn't
 * need it). This is the core of the "better than naive OSS usage" claim:
 * a plain `readBarcodes(videoFrame)` call only ever sees the raw sensor
 * image; this function gives the decoder a version of the frame tailored
 * to the specific lighting problem detected.
 */
export function enhanceForCondition(
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  quality: Pick<FrameQuality, "condition" | "focusScore">,
  aggressive: boolean,
): Uint8ClampedArray {
  switch (quality.condition) {
    case "glare": {
      const clipped = softClipHighlights(luma, aggressive ? 195 : 215);
      const flattened = flattenIllumination(clipped, width, height, aggressive ? 1.9 : 1.5);
      return unsharpMask(flattened, width, height, 0.5);
    }
    case "low-light": {
      // Denoise *before* the contrast boost, not after: CLAHE amplifies
      // whatever local contrast it finds, noise included, and by the time
      // unsharp runs that noise has already been turned into crisp fake
      // edges a linear-code checksum can occasionally be fooled by.
      const denoised = medianDenoise3x3(luma, width, height);
      const flattened = flattenIllumination(denoised, width, height, aggressive ? 2.1 : 1.7);
      const eq = clahe(flattened, width, height, { tiles: 8, clipLimit: aggressive ? 4 : 3 });
      return unsharpMask(eq, width, height, 0.7);
    }
    case "mixed": {
      const denoised = medianDenoise3x3(luma, width, height);
      const clipped = softClipHighlights(denoised, 220);
      const flattened = flattenIllumination(clipped, width, height, 1.6);
      return unsharpMask(flattened, width, height, 0.6);
    }
    case "blurry": {
      // A fixed small unsharp radius only ever sharpens near-focus blur.
      // Recovering a genuinely defocused/motion-blurred frame needs a
      // reference blur radius comparable to the blur itself — picked here
      // from the measured focus score (empirically calibrated: a synthetic
      // frame blurred past raw-decode failure only came back with a
      // radius/amount pair sized to match, see enhance/sharpen.ts).
      const { radius, amount } = pickSharpenParams(quality.focusScore, aggressive);
      return unsharpMask(luma, width, height, amount, radius);
    }
    default:
      return luma;
  }
}

function pickSharpenParams(focusScore: number, aggressive: boolean): { radius: number; amount: number } {
  const bump = aggressive ? 1 : 0;
  if (focusScore >= 8) return { radius: 3, amount: 1 + bump * 0.5 };
  if (focusScore >= 3) return { radius: 6, amount: 2 + bump * 0.5 };
  if (focusScore >= 1.2) return { radius: 12, amount: 3.5 + bump };
  return { radius: 16, amount: 4 + bump };
}
